/**
 * 発注 (purchase_order) の型定義。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §2.2, §6
 *
 * 物理コレクション: deals/{dealId}/purchase_orders/{poId}
 * - onInvoicePaid (paid 確定) → 自動起票 (status=pending)
 * - 入荷・検品フロー (2026-10-30〜):
 *     pending (未発注) → ordered (発注済・入荷待ち) → received (入荷済・検品待ち)
 *     → inspected (検品済・発送待ち) → shipped (顧客へ発送済)
 *   shipped に更新 → onPurchaseOrderShipped で deliveries 起票 + 顧客へ出荷通知 + 商談を「発送・納品」へ
 */
import type { IsoOrTimestamp } from './common';

/**
 * 発注の状態。
 * - shipped は 2026-10-30 に意味が変わった: 旧「メーカー→自社の出荷済」→ 新「自社→顧客へ発送済」。
 *   本番には旧 shipped のデータが無いことを確認済み (2026-10-30 時点で ordered 4 件 / pending 3 件のみ)。
 */
export type PurchaseOrderStatus =
  | 'pending'
  | 'ordered'
  | 'received'
  | 'inspected'
  | 'shipped'
  | 'cancelled';

export const PURCHASE_ORDER_STATUS_LABEL: Record<PurchaseOrderStatus, string> = {
  pending: '未発注',
  ordered: '発注済（入荷待ち）',
  received: '入荷済（検品待ち）',
  inspected: '検品済（発送待ち）',
  shipped: '発送済',
  cancelled: 'キャンセル',
};

/**
 * 入荷・検品で扱う機器の種類 (発注明細 1 行ごと)。
 * - tw_tabletop / tw_mobile: TimeWaver のハードウェア。付属品チェック + シリアル + 保証期間
 * - dell_pc: Dell PC。サービスタグ (= 機器の番号として serialNumber に入れる) + エクスプレスコード
 * - other_device: その他の機器。シリアルのみ
 * - none: 機器ではない (ソフト・アップグレード等)。入力なし・所有商品にも登録しない
 */
export type FulfillmentDeviceKind =
  | 'tw_tabletop'
  | 'tw_mobile'
  | 'dell_pc'
  | 'other_device'
  | 'none';

/** 付属品チェックの項目。 */
export type InspectionAccessoryKey = 'body' | 'power_cable' | 'usb_cable';

/** 検品の 1 台分。 */
export interface PurchaseOrderInspectionUnit {
  /** シリアル番号。dell_pc ではサービスタグを入れる。 */
  serialNumber: string;
  /** 付属品チェック (tw_tabletop / tw_mobile のみ)。 */
  accessories?: Partial<Record<InspectionAccessoryKey, boolean>> | null;
  /** 保証開始日・終了日 (YYYY-MM-DD、tw_tabletop / tw_mobile のみ)。 */
  warrantyStartAt?: string | null;
  warrantyEndAt?: string | null;
  /** エクスプレスコード (dell_pc のみ)。 */
  expressCode?: string | null;
}

/** 検品の明細 1 行分 (PurchaseOrder.lineItems と同じ並び)。 */
export interface PurchaseOrderInspectionLine {
  /** null = 商品名から自動で決められず、担当者がまだ選んでいない。 */
  deviceKind: FulfillmentDeviceKind | null;
  /** 台数分 (none のときは空配列)。 */
  units: PurchaseOrderInspectionUnit[];
}

/** 入荷・検品の記録 (2026-10-30〜)。 */
export interface PurchaseOrderInspection {
  /** 入荷日 (YYYY-MM-DD)。入れると status が received になる。 */
  arrivedAt: string | null;
  /** 「検品済（外観・付属品・動作を確認した）」のチェック。 */
  inspected: boolean;
  /** 検品日 (YYYY-MM-DD)。 */
  inspectedAt: string | null;
  inspectorUid: string | null;
  /** 担当者名のスナップショット (表示用)。 */
  inspectorName: string | null;
  memo: string | null;
  lines: PurchaseOrderInspectionLine[];
}

/** 発注明細1行 (見積行を踏襲する) */
export interface PurchaseOrderLineItem {
  name: string;
  modelName: string;
  variation?: string | null;
  sku?: string | null;
  quantity: number;
  unitCostPrice: number;
  subtotalCostPrice: number;
}

export interface PurchaseOrder {
  dealId: string;
  contactId: string;
  invoiceId?: string | null;

  poNumber: string;

  status: PurchaseOrderStatus;

  /** 仕入先名 (任意) */
  supplierName?: string | null;
  /** 仕入先連絡先 */
  supplierContact?: string | null;

  lineItems: PurchaseOrderLineItem[];

  /** 仕入合計 */
  totalCostPrice: number;

  /** 発注日 (status=ordered 時) */
  orderedAt: IsoOrTimestamp | null;
  /** 顧客へ発送した日時 (status=shipped 時)。2026-10-30 まではメーカー出荷の日時だった。 */
  shippedAt: IsoOrTimestamp | null;

  // ─── 入荷・検品フロー (2026-10-30〜) ───
  /** 入荷・検品の記録。未入荷は null / undefined。 */
  inspection?: PurchaseOrderInspection | null;
  /** 顧客への発送で使う配送業者・追跡番号 (shipped にするときに入れる)。 */
  shipCarrierName?: string | null;
  shipTrackingNumber?: string | null;

  assigneeUid: string | null;

  remarks?: string | null;

  // ─── v3.1.8 (TimeWaver 発注フロー拡張) ───
  /** 採用したメーカー見積 ID (deals/{dealId}/manufacturer_quotes/{id})。 */
  manufacturerQuoteId?: string | null;
  /** メーカー見積の合計金額 (税込、割引前) スナップショット。 */
  manufacturerTotalAmount?: number | null;
  /** メーカー見積のインボイス番号スナップショット。 */
  manufacturerInvoiceNumber?: string | null;
  /** ユーザーが入力した割引額 (税込)。送金額 = manufacturerTotalAmount - discountAmount */
  discountAmount?: number | null;
  /**
   * 最終インボイス PDF の Storage パス (deals/{dealId}/purchase_orders/*.pdf)。
   * 画面上の呼び名は「最終インボイス」(2026-10 に「正式発注書」から改称。フィールド名は互換のため据え置き)。
   */
  officialOrderPdfPath?: string | null;
  officialOrderUploadedAt?: IsoOrTimestamp | null;
  /** 最終インボイスの元ファイル名 (表示用、2026-10+)。未設定の旧データは Storage パスから推定する。 */
  officialOrderFileName?: string | null;
  /** 最終インボイスを登録した admin uid (2026-10+)。 */
  officialOrderUploadedBy?: string | null;
  /**
   * DHL 輸入関税書類・その他関連書類 (2026-10+)。
   * PDF 本体は Storage `deals/{dealId}/purchase_orders/{poId}/import/*`。
   */
  importDocuments?: PurchaseOrderImportDocument[] | null;
  /** 経理に送金申請が送信された時刻。null = 未申請。 */
  transferRequestedAt?: IsoOrTimestamp | null;
  /** 送金申請時の備考。 */
  transferMemo?: string | null;
  /**
   * メーカーへ「見積依頼 (発注)」メールを送信した時刻 (v3.3.x+)。
   * null / undefined = 未送信。発注カードの「メーカー見積済み」ラベル表示に使う。
   */
  manufacturerQuoteRequestedAt?: IsoOrTimestamp | null;

  createdAt: IsoOrTimestamp;
  createdBy: string;
  updatedAt: IsoOrTimestamp;
}

/** DHL 書類の種類。import_duty = 輸入関税書類 / other = その他関連書類。 */
export type ImportDocumentType = 'import_duty' | 'other';

export const IMPORT_DOCUMENT_TYPE_LABEL: Record<ImportDocumentType, string> = {
  import_duty: '輸入関税書類',
  other: 'その他関連書類',
};

/** 発注に紐付く DHL 書類 1 件 (PurchaseOrder.importDocuments の要素)。 */
export interface PurchaseOrderImportDocument {
  /** 配列内の識別子 (種類変更・削除の対象特定に使う)。 */
  id: string;
  type: ImportDocumentType;
  storagePath: string;
  fileName: string;
  /** 配列要素のため serverTimestamp は使えず、クライアントの Timestamp.now()。 */
  uploadedAt: IsoOrTimestamp;
  uploadedBy: string;
}

/**
 * メーカー見積書 (v3.1.8 新規、TimeWaver 主軸カテゴリの発注フロー用)。
 * 画面上の呼び名は「プロフォーマインボイス」(2026-10 に「メーカー見積書」から改称)。
 *
 * 物理コレクション: deals/{dealId}/manufacturer_quotes/{id}
 *
 * フロー:
 *   1. メーカーから受領した PDF をアップロード (`uploadManufacturerQuote` Callable)
 *   2. Claude AI が PDF を解析して invoiceNumber / totalAmount を抽出
 *   3. ユーザーが内容確認 + 割引額入力
 *   4. 「発注」ボタン → `confirmPurchaseOrder` で本見積を採用 (status=processed)
 *   5. PurchaseOrder.manufacturerQuoteId に紐付く
 *
 * 1 商談で複数の見積比較を許容するため複数レコード保持可。1 PO に紐付くのは 1 レコードのみ。
 */
export type ManufacturerQuoteStatus = 'draft' | 'processed';

export interface ManufacturerQuote {
  status: ManufacturerQuoteStatus;
  /**
   * 紐付ける請求書 (deals/{dealId}/invoices/{id})。
   * 「この見積書はどの請求の入金に対する仕入か」を追跡するため。
   * null = 未紐付け（運用上は非推奨。Phase C 以前の既存データは null のまま温存）。
   */
  invoiceId?: string | null;
  /** Claude AI 抽出 (失敗時は null、ユーザーが手動編集可能) */
  invoiceNumber: string | null;
  /** Claude AI 抽出の税込合計 (失敗時は null、ユーザーが手動編集可能) */
  totalAmount: number | null;
  /** ユーザー入力の割引額 (税込)。デフォルト 0 */
  discountAmount: number;
  notes?: string | null;
  /** Storage パス */
  pdfStoragePath: string;
  /** 表示用の元ファイル名 */
  originalFileName: string;
  uploadedAt: IsoOrTimestamp;
  uploadedBy: string;
  /** AI 抽出完了時刻。null は未抽出 or エラー */
  extractedAt: IsoOrTimestamp | null;
  extractionError: string | null;
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
}
